import { compose, map, set } from 'lodash/fp';
import { flow } from 'fp-ts/function';
import { array } from 'fp-ts';
import { createAction, composeProps } from '@mews/framework';
import { ApplicationState, Payment, CommanderThunkAction } from '@mews-commander/types';
import { PaymentType } from '@mews-commander/data-access';
import parentSelector from '../selector';
import prefix from '../prefix';
import { currentEnterpriseIdPropsExtender } from '../selectors/session/enterprise';
import {
    createScopedEntitiesReducer,
    createScopedBaseEntitySelectors,
    createEntityPropertyIncludesSelector,
    createEntitySelectorBy,
} from './utils';
import { createCurrentEnterpriseScopedAction } from './scopedActionCreators';

export const NAME = 'payments';

export const UNSPECIFIED_CREDIT_CARD_VALUE = 'unspecified';

const PAYMENT_ADDED = prefix('PAYMENT_ADDED');
const PAYMENTS_LOADED = prefix('PAYMENTS_LOADED');
const PAYMENTS_UPDATED = prefix('PAYMENTS_UPDATED');
const PAYMENTS_DELETED = prefix('PAYMENTS_DELETED');

export const setPayments = (payments: Payment[]) =>
    createCurrentEnterpriseScopedAction(PAYMENTS_LOADED, { entities: payments });
export const setPayment = (payment: Payment) => createCurrentEnterpriseScopedAction(PAYMENT_ADDED, payment);
export const deletePayments = ({ ids, scope }: { ids: string[]; scope: string }) =>
    createAction(PAYMENTS_DELETED, ids, { scope });

export const updatePaymentsBillId =
    ({ ids, billId, enterpriseId }: { ids: string[]; billId: string; enterpriseId: string }): CommanderThunkAction =>
    (dispatch, getState) => {
        const payments = getPaymentsByIds(getState(), { ids, enterpriseId });
        const updatedPayments = map(set('billId', billId), payments);

        return dispatch(createAction(PAYMENTS_UPDATED, updatedPayments, { scope: enterpriseId }));
    };

export const paymentsReducer = createScopedEntitiesReducer({
    ADD_SUCCESS: PAYMENT_ADDED,
    FETCH_SUCCESS: PAYMENTS_LOADED,
    UPDATE_SUCCESS: PAYMENTS_UPDATED,
    DELETE_SUCCESS: PAYMENTS_DELETED,
});

export const [getPayments, getPaymentsByIds, getPaymentsByOwnerId, getPayment] = createScopedBaseEntitySelectors<
    ApplicationState,
    Payment,
    typeof NAME,
    'enterpriseId'
>(parentSelector, NAME, 'enterpriseId');

export const getPaymentsByBillIds = createEntityPropertyIncludesSelector(getPayments, 'billId', 'billIds');

export const getPaymentIdsByBillIds = compose(map('id'), getPaymentsByBillIds);

export const getPaymentsByBillId = createEntitySelectorBy('billId')(getPayments);

export const getCurrentEnterprisePaymentsByBillId = composeProps<
    ApplicationState,
    { billId: string },
    { enterpriseId: string; billId: string },
    Payment[]
    // @ts-expect-error $TSFixMeError: Argument of type '(state: ApplicationState, props: unknown) => ExtendedProp<"enterpriseId", string>' is not assignable t...
>(getPaymentsByBillId, currentEnterpriseIdPropsExtender);

export const getCurrentEnterprisePaymentIdsByBillId = compose(map('id'), getCurrentEnterprisePaymentsByBillId);

export const hasOnlyReceivableInvoicePayments = flow(
    getCurrentEnterprisePaymentsByBillId,
    array.every((payment) => payment.type === PaymentType.Invoice)
);

export const hasSomeReceivableInvoicePayments = flow(
    getCurrentEnterprisePaymentsByBillId,
    array.some((payment) => payment.type === PaymentType.Invoice)
);

const getPaymentsByServiceOrderId = createEntitySelectorBy('serviceOrderId')(getPayments);

export const getCurrentEnterprisePaymentsByServiceOrderId = composeProps<
    ApplicationState,
    { serviceOrderId: string },
    { enterpriseId: string; serviceOrderId: string },
    Payment[]
    // @ts-expect-error $TSFixMeError: Argument of type '(state: ApplicationState, props: unknown) => ExtendedProp<"enterpriseId", string>' is not assignable t...
>(getPaymentsByServiceOrderId, currentEnterpriseIdPropsExtender);
